import { Component, computed, inject, input } from '@angular/core';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  AiSalesOutputViewModel,
  parseSalesAiInputSnapshot,
  parseSalesAiOutput
} from './sales-ai-output.presenter';

@Component({
  standalone: true,
  selector: 'ain-sales-ai-output-view',
  imports: [TableModule],
  template: `
    @if (titleKey(); as title) {
      <h3 class="block-title">{{ t(title) }}</h3>
    }
    @if (model(); as vm) {
      @if (vm.summary) {
        <p class="summary">{{ t(vm.summary) }}</p>
      }
      @if (vm.badges.length) {
        <div class="badges">
          @for (badge of vm.badges; track badge.labelKey) {
            <span class="badge" [class]="badge.tone">{{ t(badge.labelKey) }}</span>
          }
        </div>
      }
      @if (vm.fields.length) {
        <dl class="fields">
          @for (field of vm.fields; track field.labelKey + field.value) {
            <div class="field-row">
              <dt>{{ labelFor(field.labelKey) }}</dt>
              <dd>{{ formatFieldValue(field.labelKey, field.value) }}</dd>
            </div>
          }
        </dl>
      }
      @if (vm.table && vm.table.rows.length > 0) {
        <p-table [value]="vm.table!.rows" styleClass="output-table">
          <ng-template pTemplate="header">
            <tr>
              @for (header of vm.table!.headerKeys; track header) {
                <th>{{ t(header) }}</th>
              }
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              @for (cell of row; track $index) {
                <td>{{ formatTableCell(vm.table!.headerKeys[$index], cell) }}</td>
              }
            </tr>
          </ng-template>
        </p-table>
      }
      @if (vm.showRaw) {
        <details class="raw">
          <summary>{{ t('sales.ai.output.showRaw') }}</summary>
          <pre>{{ vm.rawJson }}</pre>
        </details>
      }
    }
    @if (inputModel(); as inp) {
      <h3 class="block-title">{{ t('sales.ai.output.inputTitle') }}</h3>
      @if (inp.summary) {
        <p class="summary">{{ t(inp.summary) }}</p>
      }
      @if (inp.fields.length) {
        <dl class="fields">
          @for (field of inp.fields; track field.labelKey + field.value) {
            <div class="field-row">
              <dt>{{ labelFor(field.labelKey) }}</dt>
              <dd>{{ field.value }}</dd>
            </div>
          }
        </dl>
      }
      @if (inp.showRaw) {
        <details class="raw">
          <summary>{{ t('sales.ai.output.showRaw') }}</summary>
          <pre>{{ inp.rawJson }}</pre>
        </details>
      }
    }
  `,
  styles: [`
    .summary { margin: 0 0 8px; font-size: 14px; color: #111827; }
    .block-title { margin: 12px 0 8px; font-size: 14px; font-weight: 700; color: #111827; }
    .block-title:first-child { margin-top: 0; }
    .badges { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
    .badge { font-size: 12px; font-weight: 600; padding: 4px 8px; border-radius: 999px; }
    .badge.info { background: #e0e7ff; color: #3730a3; }
    .badge.success { background: #dcfce7; color: #166534; }
    .badge.warn { background: #fef3c7; color: #92400e; }
    .badge.danger { background: #fee2e2; color: #991b1b; }
    .fields { margin: 0 0 10px; display: grid; gap: 6px; }
    .field-row { display: grid; grid-template-columns: minmax(120px, 38%) 1fr; gap: 8px; font-size: 13px; }
    dt { margin: 0; color: #6b7280; }
    dd { margin: 0; font-weight: 600; color: #111827; }
    .raw { margin-top: 8px; }
    .raw summary { cursor: pointer; font-size: 12px; color: #6b7280; }
    pre { background: #f3f4f6; padding: 8px; border-radius: 8px; font-size: 11px; overflow: auto; margin-top: 6px; }
    :host ::ng-deep .output-table .p-datatable-thead > tr > th { font-size: 12px; }
    :host ::ng-deep .output-table .p-datatable-tbody > tr > td { font-size: 13px; }
  `]
})
export class SalesAiOutputViewComponent {
  private readonly localization = inject(LocalizationService);

  readonly outputJson = input<string | null | undefined>('');
  readonly inputSnapshotJson = input<string | null | undefined>(undefined);
  readonly titleKey = input<string | null>(null);

  readonly model = computed((): AiSalesOutputViewModel => parseSalesAiOutput(this.outputJson()));
  readonly inputModel = computed(() => {
    const snap = this.inputSnapshotJson();
    return snap ? parseSalesAiInputSnapshot(snap) : null;
  });

  t(key: string): string {
    return this.localization.translate(key);
  }

  labelFor(labelKey: string): string {
    const translated = this.t(labelKey);
    return translated === labelKey ? labelKey.replace('sales.ai.output.fields.', '') : translated;
  }

  formatFieldValue(labelKey: string, value: string): string {
    if (labelKey === 'sales.ai.output.fields.suggestedAction') {
      const actionKey = `sales.ai.output.actions.${value}`;
      const translated = this.t(actionKey);
      return translated === actionKey ? value : translated;
    }
    if (labelKey === 'sales.ai.output.columns.signalCode' || labelKey.endsWith('signalCode')) {
      const codeKey = `sales.ai.output.signals.${value}`;
      const translated = this.t(codeKey);
      return translated === codeKey ? value : translated;
    }
    return value;
  }

  formatTableCell(headerKey: string, cell: string): string {
    if (headerKey === 'sales.ai.output.columns.signalCode') {
      return this.formatFieldValue('sales.ai.output.columns.signalCode', cell);
    }
    if (headerKey === 'sales.ai.output.fields.suggestedAction') {
      return this.formatFieldValue('sales.ai.output.fields.suggestedAction', cell);
    }
    return cell;
  }
}
